import React, { useMemo, useState } from 'react';
import { Column, IdType, Row, useTable } from 'react-table';
import styled from 'styled-components';

import { TabPanel } from '@material-ui/lab';

import { LogItem } from '../../helpers/logger';
import DebugItem from './DebugItem';
import Filters from './Filters';

const Styles = styled.div`
  display: block;
  max-width: 100%;
  max-height: 75vh;
  overflow-y: auto;

  /* This will make the table scrollable when it gets too small */
  .tableWrap {
    display: block;
    max-width: 100%;
    overflow-x: scroll;
    overflow-y: hidden;
    border-bottom: 1px solid black;
  }

  table {
    /* Make sure the inner table is always as wide as needed */
    width: 100%;
    border-spacing: 0;

    tr {
      :last-child {
        td {
          border-bottom: 0;
        }
      }
    }
    thead {
      position: sticky;
      top: 0;
      background: #030344;
      color: white;
      z-index: 5;
      th {
        border-right: 2px solid white;
        :last-child: {
          border-right: 0;
        }
      }
    }

    th,
    td {
      margin: 0;
      padding: 0.5rem;
      border-bottom: 1px solid black;
      border-right: 1px solid black;

      /* The secret sauce */
      /* Each cell should grow equally */
      width: 1%;
      /* But "collapsed" cells should be as small as possible */
      &.collapse {
        width: 0.0000000001%;
      }

      :last-child {
        border-right: 0;
      }
    }
  }

  .pagination {
    padding: 0.5rem;
  }
`;

interface IDebugTab {
  logs: Array<LogItem>;
  tabValue: string;
}
type RowType = { [k: string]: LogItem[] };

type ColumnWithId<D extends object = {}> = Column<D> & { id: IdType<D> };

const DebugTab: React.FC<IDebugTab> = (props: IDebugTab) => {
  const { logs, tabValue } = props;
  const [filteredLogs, setFilteredLogs] = useState(logs);

  const columns: Array<ColumnWithId<RowType>> = useMemo(() => {
    const columnSet = new Set(
      filteredLogs.map((item) => item.columns).flatMap((num) => `${num}`)
    );
    const sortedColumns = Array.from(columnSet).sort((a, b) =>
      a > b ? -1 : 1
    );
    return sortedColumns.map((column) => ({
      Header: column,
      id: column,
      accessor: column,
      Cell: (everything) => {
        const {
          row,
          column,
        }: { row: Row<RowType>; column: ColumnWithId<LogItem> } = everything;
        const values = row.values;
        const items = values[column.id] ?? [];
        return <DebugItem items={items} />;
      },
    }));
  }, [filteredLogs]);

  const data: Array<RowType> = useMemo(() => {
    const timeStampArr = Array.from(
      new Set(filteredLogs.map((item) => item.timeStamp))
    );
    const dict = {};
    filteredLogs.forEach((log) => {
      dict[log.timeStamp] = dict[log.timeStamp] ?? {};
      log.columns.forEach((column) => {
        dict[log.timeStamp][column] = dict[log.timeStamp][column] ?? [];
        dict[log.timeStamp][column] = [...dict[log.timeStamp][column], log];
      });
    });

    const finalArray = timeStampArr.map((timeStamp) => {
      return columns.reduce((acc, curr) => {
        acc[curr.id] = dict[timeStamp][curr.id];
        return acc;
      }, {});
    });
    return finalArray;
  }, [filteredLogs, columns]);

  const tableInstance = useTable({ columns, data });

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    tableInstance;

  return (
    <TabPanel value={tabValue}>
      <Filters
        afterFilter={(filtered) => setFilteredLogs(filtered)}
        logs={logs}
      />
      <Styles>
        <table {...getTableProps()}>
          <thead>
            {
              // Loop over the header rows
              headerGroups.map((headerGroup) => (
                // Apply the header row props
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {
                    // Loop over the headers in each row
                    headerGroup.headers.map((column) => (
                      // Apply the header cell props
                      <th {...column.getHeaderProps()}>
                        {
                          // Render the header
                          column.render('Header')
                        }
                      </th>
                    ))
                  }
                </tr>
              ))
            }
          </thead>
          {/* Apply the table body props */}
          <tbody {...getTableBodyProps()}>
            {
              // Loop over the table rows
              rows.map((row) => {
                // Prepare the row for display
                prepareRow(row);
                return (
                  // Apply the row props
                  <tr {...row.getRowProps()}>
                    {
                      // Loop over the rows cells
                      row.cells.map((cell) => {
                        // Apply the cell props
                        return (
                          <td {...cell.getCellProps()}>
                            {
                              // Render the cell contents
                              cell.render('Cell')
                            }
                          </td>
                        );
                      })
                    }
                  </tr>
                );
              })
            }
          </tbody>
        </table>
      </Styles>
    </TabPanel>
  );
};

export default DebugTab;
